import { useTranslation } from 'react-i18next';
import { SUPPORTED_LOCALES, type Locale } from '@bolao/shared';
import styles from './LanguageToggle.module.css';

/**
 * The segmented language control.
 *
 * The app already opens in the device's language — the detector checks
 * `localStorage` first, then `navigator` — so this is only for an explicit
 * choice: on the settings screen (signed in, persisted to `users.locale` via
 * `onChange`) and on the sign-in screen (no account yet to persist to, so
 * just the local device preference). PLAN.md Phase 7.
 *
 * Changing the language here always writes to `localStorage` via the
 * detector, which then takes precedence over the device setting on future
 * visits in this browser — that part happens regardless of `onChange`.
 */
export default function LanguageToggle({ onChange }: { onChange?: (locale: Locale) => void }) {
  const { t, i18n } = useTranslation();

  return (
    <div className={styles.group} role="group" aria-label={t('language.label')}>
      {SUPPORTED_LOCALES.map((locale) => {
        const isActive = i18n.resolvedLanguage === locale;
        return (
          <button
            key={locale}
            type="button"
            className={`${styles.option} ${isActive ? styles.active : ''}`}
            aria-pressed={isActive}
            // The full language name is the accessible label; the button itself
            // shows a two-letter abbreviation to stay compact.
            aria-label={t(`language.${locale}`)}
            onClick={() => {
              void i18n.changeLanguage(locale);
              onChange?.(locale);
            }}
          >
            {t(`language.${locale}Short`)}
          </button>
        );
      })}
    </div>
  );
}
